import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import { GET_CATEGORY_PAGE } from 'mocks/queries';
import addDerivedProductData from 'hooks/useProductListingData/helpers/addDerivedProductData';
import useUserLocation from 'global-state/userLocation/useUserLocation';

const SingleCollectionCards = ({
  slug = '',
  skipProductIds = [],
  productsList = null,
  col = 4,
  modifier = '',
}) => {
  const userLocation = useUserLocation();

  const {
    data: categoryData,
    loading: categoryPageDataLoading,
    error: categoryPageDataError,
  } = useSuspenseQuery(GET_CATEGORY_PAGE, {
    variables: {
      slug,
      ...getVisitorLocationQueryVariables(userLocation?.stateShortName),
    },
  });

  const categoryProducts = useMemo(() => {
    if (productsList) {
      return productsList;
    }
    if (!categoryData?.getCategoryPage?.products) {
      return [];
    }
    return categoryData?.getCategoryPage?.products
      .filter(product => !skipProductIds.includes(product.id))
      .map(product =>
        addDerivedProductData({
          product,
          upholsteryOptions: categoryData?.getCategoryPage?.upholsteryOptions,
        })
      )
      .slice(0, 4);
  }, [
    productsList,
    skipProductIds,
    categoryData?.getCategoryPage?.products,
    categoryData?.getCategoryPage?.upholsteryOptions,
  ]);

  if (!categoryProducts.length && categoryPageDataLoading) {
    return (
      <div className="w-full flex flex-col items-center justify-center">
        <ProgressLoader />
      </div>
    );
  }

  if (
    !categoryProducts.length &&
    (!categoryPageDataLoading || categoryPageDataError)
  )
    return null;

  return (
    <div
      className={classNames({
        'max-[767px]:flex max-[767px]:min-h-[225px] max-[767px]:mt-2':
          modifier === 'modular',
      })}
    >
      <div
        className={classNames(
          'grid grid-cols-[repeat(auto-fit,minmax(100%,1fr))] gap-[10px]',
          {
            'md:m-2 md:grid-cols-[repeat(auto-fit,minmax(300px,100%))]':
              col === 1,
            'md:m-2 md:grid-cols-[repeat(auto-fit,minmax(300px,calc(50%_-_5px)))] lg:grid-cols-[repeat(auto-fit,minmax(300px,calc(33%_-_7px)))] min-[1400px]:grid-cols-[repeat(auto-fit,minmax(300px,calc(25%_-_10px)))]':
              col === 4,
            'max-[767px]:!flex max-[767px]:!flex-row max-[767px]:!flex-nowrap max-[767px]:!overflow-x-scroll':
              modifier === 'modular',
          }
        )}
      >
        {categoryProducts.map(product => (
          <div
            className={classNames({
              'max-[767px]:!flex-shrink-0 max-[767px]:!mr-2 max-[767px]:!max-w-[500px] max-[767px]:!w-4/5':
                modifier === 'modular',
            })}
          >
            <ProductCardStatic
              filterStateKey="no-filters--featured"
              isClearance={product.isClearance}
              key={product.slug}
              label={product.label}
              moreVariantsCount={product.moreVariantsCount}
              name={product.name}
              onDisplayInShowroomSlug={product.onDisplayInShowroomSlug}
              path={product.path}
              position={product.position}
              productFamily={product.productFamily}
              productLabel={product.productLabel}
              productType={product.productType}
              quickship={product.quickship}
              secondaryImageCropData={product.secondaryImageCropData}
              slug={product.slug}
              variants={product.variants}
              variantsToShow={product.variants.filter(
                (val, index) => index < 6
              )}
              variantsTotal={product.variantsTotal}
              enableMobileCarousel
              campaign={product.campaign}
              id={product.id}
              layout="full"
              secondaryImage={product.secondaryImage}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

SingleCollectionCards.propTypes = {
  slug: PropTypes.string,
  skipProductIds: PropTypes.arrayOf(PropTypes.number),
  productsList: PropTypes.arrayOf(PropTypes.object),
  col: PropTypes.number,
  modifier: PropTypes.string,
};

export default SingleCollectionCards;
